import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { logError } from '@/lib/log';
import { useAuth } from '../context/AuthContext';
import { Auth } from '../components/ui/Auth';
import { OnboardingDarkSplash } from '../components/onboarding/OnboardingDarkSplash';
import { AuthenticatedApp } from './AuthenticatedApp';
import { PaidAccessGate } from '../components/subscription/PaidAccessGate';
import { recordSessionStartEvent } from '@shared/services/analyticsService';
import { identifyUser, clearUser, trackEvent, setAnalyticsUserProperty } from '@shared/services/analyticsTracker';
import { markInternalDevice } from '@shared/services/attributionService';
import { markOnboardingCompletedLocally } from '../hooks/useOnboardingRedirect';
import { useNavigate, useLocation } from 'react-router';
import { Capacitor } from '@capacitor/core';
import { getActiveEntitlements } from '../services/purchasesService';
import { isRestorableStoreForPlatform, markOrphanedPurchaseHint } from '../lib/paidLaunch';
import { trackStrandedTrialConversion } from '../lib/strandedPurchaseConversion';
// Deep-link allowlist is DERIVED from the route table — one source of truth, so
// a new page in pageRoutes is automatically deep-linkable (no drift). Used below
// to verify a post-login intended path; unknown paths fall back to home.
import { isDeepLinkableRoute } from '../pages';

const SESSION_STARTED_KEY = 'enough_session_started';

// The pre-login entry: the value carousel (sell before sign-up). Lazy so the
// chunk only loads for logged-out visitors; returning users never fetch it.
const ValueOnboardingEntry = lazy(() =>
    import('../experiments/onboarding-entry/ValueOnboardingEntry').then(m => ({ default: m.ValueOnboardingEntry })),
);

export const AuthGuard: React.FC = () => {
    const { currentUser, isAuthenticated, isNewSignup, clearNewSignup, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Track whether user was previously unauthenticated so we only navigate
    // to home on actual login/signup, not on page refresh
    const wasUnauthenticated = useRef(false);
    // Remember where the user was trying to go before login
    const intendedPath = useRef<string | null>(null);

    // Keep intendedPath current on every render so the effect below doesn't
    // need location.pathname in its dependency array (which caused the
    // stuck-navigation bug reverted from 5df83cf9).
    if (!loading && !isAuthenticated && location.pathname !== '/login') {
        intendedPath.current = location.pathname;
    }

    // Navigate to intended destination (or home) when user logs in
    useEffect(() => {
        if (loading) return;
        if (!isAuthenticated) {
            wasUnauthenticated.current = true;
        } else if (wasUnauthenticated.current) {
            wasUnauthenticated.current = false;

            if (isNewSignup) {
                const providerId = currentUser!.providerData[0]?.providerId ?? 'unknown';
                const methodMap: Record<string, string> = {
                    'google.com': 'google',
                    'apple.com': 'apple',
                    'password': 'email',
                };
                trackEvent('sign_up', { method: methodMap[providerId] ?? providerId });
            } else {
                // Returning user signing in — skip onboarding, home cards catch missing info
                markOnboardingCompletedLocally(currentUser!.uid);
            }

            // New users go straight to onboarding; returning users go to intended path or home
            let destination: string;
            if (isNewSignup) {
                destination = '/welcome';
            } else {
                destination = intendedPath.current && isDeepLinkableRoute(intendedPath.current)
                    ? intendedPath.current
                    : '/';
            }
            intendedPath.current = null;
            if (isNewSignup) clearNewSignup();
            navigate(destination, { replace: true });
        }
    }, [isAuthenticated, loading]);

    // Identify user for GA4 + Fullstory + PostHog on auth state change (only for real users)
    useEffect(() => {
        if (!isAuthenticated || !currentUser) {
            clearUser();
            return;
        }
        const platform = Capacitor.getPlatform() as 'web' | 'ios' | 'android';
        identifyUser(currentUser.uid, {
            platform,
            app_version: import.meta.env.VITE_APP_VERSION ?? '',
            email: currentUser.email ?? '',
        });
        // Internal users taint the device so future anonymous sessions here are
        // excluded from marketing metrics (the Auth claim can't cover pre-login).
        // The same claim becomes the `internal_user_flag` user property — the
        // rollup already excludes internal uids, but GA4 only can if the client
        // says so (the prod Remote Config "Internal Users" condition and the
        // GA4 exclusion segment both match `'true'`). Written both ways so a
        // device that switches accounts does not stay flagged.
        currentUser.getIdTokenResult()
            .then(result => {
                const internal = !!result.claims.isInternalUser;
                setAnalyticsUserProperty('internal_user_flag', String(internal));
                if (internal) markInternalDevice();
            })
            .catch(logError);
    }, [isAuthenticated, currentUser]);

    // Session start: fire once per tab when real user authenticates
    useEffect(() => {
        if (!isAuthenticated || !currentUser) return;
        const key = `${SESSION_STARTED_KEY}_${currentUser.uid}`;
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, 'true');
        recordSessionStartEvent(currentUser.uid).catch(logError);
    }, [isAuthenticated, currentUser]);

    // Deep link from push notification taps
    useEffect(() => {
        const handler = (e: Event) => {
            const data = (e as CustomEvent).detail;
            if (data?.screen) {
                const screenRoutes: Record<string, string> = {
                    home: '/',
                    reflect: '/reflect',
                    grow: '/grow',
                    story: '/story',
                    goals: '/goals',
                    accounts: '/connect',
                    journal: '/journal',
                    profile: '/profile',
                    no_spend_weekend: '/pause?tab=shopping-break',
                    cart_companion: '/pause?tab=pre-purchase',
                };
                // A cart-companion reminder carries the specific itemId so we can open its
                // review sheet directly.
                if (data.screen === 'cart_companion' && data.itemId) {
                    navigate(`/pause?tab=pre-purchase&item=${encodeURIComponent(data.itemId)}`);
                    return;
                }
                const route = screenRoutes[data.screen];
                if (route) navigate(route);
            }
        };
        window.addEventListener('notification-navigate', handler);
        return () => window.removeEventListener('notification-navigate', handler);
    }, [navigate]);

    // Redirect authenticated users away from /login
    useEffect(() => {
        if (isAuthenticated && location.pathname === '/login') {
            navigate('/', { replace: true });
        }
    }, [isAuthenticated, location.pathname, navigate]);

    // Stranded-payer resume (paid launch, native): a visitor who bought at the
    // pre-login paywall but killed the app before creating an account is being
    // billed by the store with no way into the product — on relaunch they'd
    // just see the entry carousel again. If the ANONYMOUS RevenueCat user
    // holds an active entitlement, route them straight back to sign-up.
    const strandedCheckDone = useRef(false);
    useEffect(() => {
        if (loading || isAuthenticated || !currentUser?.isAnonymous) return;
        if (!Capacitor.isNativePlatform()) return;
        if (location.pathname === '/login') return; // already headed to auth
        if (strandedCheckDone.current) return;
        strandedCheckDone.current = true;
        const anonUid = currentUser.uid;
        getActiveEntitlements().then((entitlements) => {
            if (entitlements.length === 0) return;
            // Device-local breadcrumb: if the visitor signs IN to an existing
            // account instead (different uid), PaidAccessGate reads this and
            // leads with Restore purchases instead of a fresh sell. Only
            // stamped when the entitlement's store matches THIS device's
            // store — Restore replays this device's store receipts, so a
            // PROMOTIONAL/STRIPE entitlement (or an Apple purchase seen on
            // Android) would make the button a dead end.
            const restorable = entitlements.find((e) =>
                isRestorableStoreForPlatform(e.store, Capacitor.getPlatform()),
            );
            if (restorable) markOrphanedPurchaseHint(anonUid, restorable.store);
            trackEvent('value_onboarding_stranded_purchase_resume', {
                restorable: !!restorable,
            });
            // The kill that stranded them also skipped the point-of-sale
            // conversion; send it now (fire-and-forget — never delay the resume).
            trackStrandedTrialConversion(anonUid, entitlements).catch(logError);
            // The check is async — if the user has meanwhile navigated to the
            // auth screen themselves, don't yank them out of a form they're
            // mid-way through; the hint above still covers the sign-in path.
            if (window.location.pathname === '/login') return;
            navigate('/login?mode=signup&postPurchase=1', { replace: true });
        }).catch(logError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, isAuthenticated, currentUser?.isAnonymous]);

    // Calm dark splash (no spinner) covers the auth-init + entry-chunk waits,
    // so the first thing a logged-out visitor sees is a branded screen — not a
    // loading circle. It flows seamlessly into the carousel's dark intro.
    // Returning users (have the local onboarding-completed marker) head into the
    // light app, so they get a light loader instead of the dark entry splash.
    const isReturningUser = (() => {
        try { return !!localStorage.getItem('enough_onboarding_completed_uid'); } catch { return false; }
    })();
    const lightLoader = (
        <div className="h-screen w-full flex flex-col items-center justify-center bg-sand">
            <div className="w-16 h-16 border-4 border-mist border-t-foliage rounded-full animate-spin"></div>
            <p className="mt-4 text-growth font-serif italic animate-pulse">Breathing...</p>
        </div>
    );
    const entryLoader = isReturningUser ? lightLoader : <OnboardingDarkSplash />;

    // Show loading while checking auth state
    if (loading) {
        return entryLoader;
    }

    // Auth init failed outright: no user at all (anonymous sign-in exhausted
    // its retries — AuthContext). This state can otherwise never resolve
    // (onAuthStateChanged won't fire again), and every downstream surface
    // waits on a uid — the old behavior was a PERMANENT dark splash with
    // nothing to tap (seen on-device 2026-07-21). Say what's wrong and offer
    // the one action that actually restarts auth init: a full reload.
    if (!currentUser) {
        return (
            <div className="h-screen w-full flex flex-col items-center justify-center bg-sand p-6 text-center">
                <h1 className="text-xl font-serif text-growth mb-2">We couldn&apos;t connect</h1>
                <p className="text-sm text-growth/70 mb-6 max-w-xs">
                    Something got in the way while starting up. Check your connection and try again.
                </p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-3 rounded-xl bg-growth text-cloud font-medium hover:bg-growth/90 transition-colors"
                >
                    Try again
                </button>
            </div>
        );
    }

    // Authenticated (non-anonymous) users see the full app. The PaidAccessGate
    // holds users with no active entitlement (trial expired / canceled / never
    // started) on the paywall instead.
    if (isAuthenticated) {
        if (location.pathname === '/login') return null; // redirecting via effect above
        return (
            <PaidAccessGate>
                <AuthenticatedApp />
            </PaidAccessGate>
        );
    }

    // /login shows the Auth form; everything else shows the Welcome splash
    if (location.pathname === '/login') {
        const params = new URLSearchParams(location.search);
        // `postPurchase=1` still rides the URL from the pre-login paywall as a
        // breadcrumb, but renders nothing — the slot above the auth form is
        // reserved for errors only (Justin, 2026-07-21). It IS forwarded as an
        // analytics dimension: a paid-but-stalled-here visitor is the worst
        // possible drop-off, so the auth_shown/auth_error events carry it.
        return (
            <Auth
                defaultMode={params.get('mode') === 'signup' ? 'signup' : 'login'}
                postPurchase={params.get('postPurchase') === '1'}
            />
        );
    }

    // Pre-login entry: the value carousel is the one champion since the
    // `onboarding_entry` slot retired (2026-09, core-loop redesign decision 4).
    // The dark splash matches the entry loader, so chunk load is a seamless wait.
    return (
        <Suspense fallback={<OnboardingDarkSplash />}>
            <ValueOnboardingEntry />
        </Suspense>
    );
};
